import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { Plus, Search, Pencil, Trash2, ClipboardList, Pill, Activity, Calendar, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'emergencia', label: 'Emergencia', color: 'bg-red-100 text-red-800' },
  { value: 'hospitalizacion', label: 'Hospitalización', color: 'bg-blue-100 text-blue-800' },
  { value: 'ambulatorio', label: 'Ambulatorio', color: 'bg-green-100 text-green-800' },
  { value: 'uci', label: 'UCI', color: 'bg-purple-100 text-purple-800' },
  { value: 'paliativo', label: 'Paliativo', color: 'bg-gray-100 text-gray-800' },
];

const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[2];

export default function TreatmentPlansPage() {
  const { user, hasPermission, isSuperAdmin, isInstructor } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    categoria: 'ambulatorio',
    descripcion: '',
    diagnosticos_asociados: [],
    medicamentos: [],
    indicaciones: [],
    controles: [],
    derivaciones: [],
    notas: ''
  });

  // Temp inputs for adding items
  const [newMedicamento, setNewMedicamento] = useState({ nombre: '', dosis: '', frecuencia: '', via: 'IV', indicacion: '' });
  const [newIndicacion, setNewIndicacion] = useState({ tipo: 'cuidados', descripcion: '', prioridad: 'normal' });
  const [newControl, setNewControl] = useState({ tipo: 'laboratorio', descripcion: '', frecuencia: '' });
  const [newDerivacion, setNewDerivacion] = useState({ especialidad: '', motivo: '', urgencia: 'normal' });

  const canCreate = hasPermission('tratamientos', 'crear') || isSuperAdmin || isInstructor;
  const canEdit = hasPermission('tratamientos', 'editar') || isSuperAdmin || isInstructor;
  const canDelete = hasPermission('tratamientos', 'eliminar') || isSuperAdmin;

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/treatment-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data);
    } catch (error) {
      toast.error('Error al cargar planes de tratamiento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const resetForm = () => {
    setFormData({
      nombre: '',
      categoria: 'ambulatorio',
      descripcion: '',
      diagnosticos_asociados: [],
      medicamentos: [],
      indicaciones: [],
      controles: [],
      derivaciones: [],
      notas: ''
    });
    setNewMedicamento({ nombre: '', dosis: '', frecuencia: '', via: 'IV', indicacion: '' });
    setNewIndicacion({ tipo: 'cuidados', descripcion: '', prioridad: 'normal' });
    setNewControl({ tipo: 'laboratorio', descripcion: '', frecuencia: '' });
    setNewDerivacion({ especialidad: '', motivo: '', urgencia: 'normal' });
  };

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        nombre: plan.nombre || '',
        categoria: plan.categoria || 'ambulatorio',
        descripcion: plan.descripcion || '',
        diagnosticos_asociados: plan.diagnosticos_asociados || [],
        medicamentos: plan.medicamentos || [],
        indicaciones: plan.indicaciones || [],
        controles: plan.controles || [],
        derivaciones: plan.derivaciones || [],
        notas: plan.notas || ''
      });
    } else {
      setEditingPlan(null);
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPlan(null);
  };

  // Add item handlers
  const handleAddMedicamento = () => {
    if (newMedicamento.nombre && newMedicamento.dosis) {
      setFormData(prev => ({
        ...prev,
        medicamentos: [...prev.medicamentos, { ...newMedicamento }]
      }));
      setNewMedicamento({ nombre: '', dosis: '', frecuencia: '', via: 'IV', indicacion: '' });
    }
  };

  const handleAddIndicacion = () => {
    if (newIndicacion.descripcion) {
      setFormData(prev => ({
        ...prev,
        indicaciones: [...prev.indicaciones, { ...newIndicacion }]
      }));
      setNewIndicacion({ tipo: 'cuidados', descripcion: '', prioridad: 'normal' });
    }
  };

  const handleAddControl = () => {
    if (newControl.descripcion) {
      setFormData(prev => ({
        ...prev,
        controles: [...prev.controles, { ...newControl }]
      }));
      setNewControl({ tipo: 'laboratorio', descripcion: '', frecuencia: '' });
    }
  };

  const handleAddDerivacion = () => {
    if (newDerivacion.especialidad && newDerivacion.motivo) {
      setFormData(prev => ({
        ...prev,
        derivaciones: [...prev.derivaciones, { ...newDerivacion }]
      }));
      setNewDerivacion({ especialidad: '', motivo: '', urgencia: 'normal' });
    }
  };

  // Remove item handlers
  const removeItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingPlan) {
        await axios.put(`${API_URL}/api/treatment-plans/${editingPlan.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plan de tratamiento actualizado');
      } else {
        await axios.post(`${API_URL}/api/treatment-plans`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plan de tratamiento creado');
      }
      handleCloseModal();
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar plan');
    }
  };

  const handleDelete = async (plan) => {
    if (plan.is_default) {
      toast.error('No se pueden eliminar planes del sistema');
      return;
    }
    if (!window.confirm(`¿Eliminar plan "${plan.nombre}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/treatment-plans/${plan.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan eliminado');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar plan');
    }
  };

  const filteredPlans = plans.filter(p => {
    const matchesSearch = (p.nombre || '').toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesCategory = selectedCategory === 'all' || p.categoria === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="p-6 space-y-6" data-testid="treatment-plans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="h-7 w-7 text-green-600" />
            Planes de Tratamiento
          </h1>
          <p className="text-gray-500">Plantillas de planes de tratamiento para simulaciones</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="bulk-import-link-treatments"
            onClick={() => window.location.assign('/app/admin/bulk-import?module=treatments')}>
            Carga Masiva
          </Button>
          {canCreate && (
            <Button onClick={() => handleOpenModal()} data-testid="add-plan-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Plan
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar planes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-plans"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[200px]" data-testid="category-filter">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Plans Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 h-48 bg-gray-100" />
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredPlans.map(plan => {
            const catInfo = getCategoryInfo(plan.categoria);
            return (
              <Card key={plan.id} className="hover:shadow-md transition-shadow" data-testid={`plan-card-${plan.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{plan.nombre}</CardTitle>
                      <Badge variant="secondary" className={`mt-1 ${catInfo.color}`}>
                        {catInfo.label}
                      </Badge>
                    </div>
                    {plan.is_default && <Badge variant="outline">Sistema</Badge>}
                  </div>
                </CardHeader>
                <CardContent>
                  {plan.descripcion && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{plan.descripcion}</p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Pill className="h-4 w-4" />
                      <span>{plan.medicamentos?.length || 0} medicamentos</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Activity className="h-4 w-4" />
                      <span>{plan.indicaciones?.length || 0} indicaciones</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>{plan.controles?.length || 0} controles</span>
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <Users className="h-4 w-4" />
                      <span>{plan.derivaciones?.length || 0} derivaciones</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {canEdit && !plan.is_default && (
                      <Button variant="outline" size="sm" onClick={() => handleOpenModal(plan)}>
                        <Pencil className="h-3 w-3 mr-1" />
                        Editar
                      </Button>
                    )}
                    {canDelete && !plan.is_default && (
                      <Button variant="outline" size="sm" onClick={() => handleDelete(plan)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-3 w-3 mr-1" />
                        Eliminar
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && filteredPlans.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No se encontraron planes de tratamiento</p>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Editar Plan de Tratamiento' : 'Nuevo Plan de Tratamiento'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre del Plan *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                  placeholder="Ej: Manejo de IAM STEMI"
                  required
                  data-testid="input-plan-nombre"
                />
              </div>
              <div>
                <Label>Categoría *</Label>
                <Select value={formData.categoria} onValueChange={(v) => setFormData(prev => ({ ...prev, categoria: v }))}>
                  <SelectTrigger data-testid="select-plan-categoria">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Descripción del plan de tratamiento"
                rows={2}
              />
            </div>

            <Accordion type="multiple" className="w-full">
              {/* Medicamentos */}
              <AccordionItem value="medicamentos">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Pill className="h-4 w-4" />
                    Medicamentos ({formData.medicamentos.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="grid grid-cols-5 gap-2">
                    <Input placeholder="Nombre" value={newMedicamento.nombre} onChange={(e) => setNewMedicamento(prev => ({ ...prev, nombre: e.target.value }))} />
                    <Input placeholder="Dosis" value={newMedicamento.dosis} onChange={(e) => setNewMedicamento(prev => ({ ...prev, dosis: e.target.value }))} />
                    <Input placeholder="Frecuencia" value={newMedicamento.frecuencia} onChange={(e) => setNewMedicamento(prev => ({ ...prev, frecuencia: e.target.value }))} />
                    <Select value={newMedicamento.via} onValueChange={(v) => setNewMedicamento(prev => ({ ...prev, via: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IV">IV</SelectItem>
                        <SelectItem value="VO">VO</SelectItem>
                        <SelectItem value="IM">IM</SelectItem>
                        <SelectItem value="SC">SC</SelectItem>
                        <SelectItem value="Nebulización">Nebulización</SelectItem>
                        <SelectItem value="Naricera">Naricera</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" onClick={handleAddMedicamento}><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-2">
                    {formData.medicamentos.map((med, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">
                          <strong>{med.nombre}</strong> {med.dosis} - {med.frecuencia} ({med.via})
                        </span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem('medicamentos', idx)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Indicaciones */}
              <AccordionItem value="indicaciones">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Indicaciones ({formData.indicaciones.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="grid grid-cols-4 gap-2">
                    <Select value={newIndicacion.tipo} onValueChange={(v) => setNewIndicacion(prev => ({ ...prev, tipo: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dieta">Dieta</SelectItem>
                        <SelectItem value="actividad">Actividad</SelectItem>
                        <SelectItem value="cuidados">Cuidados</SelectItem>
                        <SelectItem value="monitoreo">Monitoreo</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input className="col-span-2" placeholder="Descripción" value={newIndicacion.descripcion} onChange={(e) => setNewIndicacion(prev => ({ ...prev, descripcion: e.target.value }))} />
                    <Button type="button" onClick={handleAddIndicacion}><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-2">
                    {formData.indicaciones.map((ind, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">
                          <Badge variant="outline" className="mr-2">{ind.tipo}</Badge>
                          {ind.descripcion}
                        </span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem('indicaciones', idx)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Controles */}
              <AccordionItem value="controles">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Controles ({formData.controles.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="grid grid-cols-4 gap-2">
                    <Select value={newControl.tipo} onValueChange={(v) => setNewControl(prev => ({ ...prev, tipo: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="laboratorio">Laboratorio</SelectItem>
                        <SelectItem value="imagen">Imagen</SelectItem>
                        <SelectItem value="consulta">Consulta</SelectItem>
                        <SelectItem value="signos_vitales">Signos Vitales</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Descripción" value={newControl.descripcion} onChange={(e) => setNewControl(prev => ({ ...prev, descripcion: e.target.value }))} />
                    <Input placeholder="Frecuencia" value={newControl.frecuencia} onChange={(e) => setNewControl(prev => ({ ...prev, frecuencia: e.target.value }))} />
                    <Button type="button" onClick={handleAddControl}><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-2">
                    {formData.controles.map((ctrl, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">
                          <Badge variant="outline" className="mr-2">{ctrl.tipo}</Badge>
                          {ctrl.descripcion} {ctrl.frecuencia && `- ${ctrl.frecuencia}`}
                        </span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem('controles', idx)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Derivaciones */}
              <AccordionItem value="derivaciones">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Derivaciones ({formData.derivaciones.length})
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="grid grid-cols-4 gap-2">
                    <Input placeholder="Especialidad" value={newDerivacion.especialidad} onChange={(e) => setNewDerivacion(prev => ({ ...prev, especialidad: e.target.value }))} />
                    <Input placeholder="Motivo" value={newDerivacion.motivo} onChange={(e) => setNewDerivacion(prev => ({ ...prev, motivo: e.target.value }))} />
                    <Select value={newDerivacion.urgencia} onValueChange={(v) => setNewDerivacion(prev => ({ ...prev, urgencia: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urgente">Urgente</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="electiva">Electiva</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" onClick={handleAddDerivacion}><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-2">
                    {formData.derivaciones.map((der, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">
                          <strong>{der.especialidad}</strong> - {der.motivo}
                          <Badge variant="outline" className="ml-2">{der.urgencia}</Badge>
                        </span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem('derivaciones', idx)}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div>
              <Label>Notas adicionales</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
                placeholder="Notas o comentarios adicionales"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="submit-plan">
                {editingPlan ? 'Guardar Cambios' : 'Crear Plan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
